import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bars3Icon, 
  BellIcon, 
  UserCircleIcon,
  MagnifyingGlassIcon,
  UserIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
// ✅ PHASE 4: Import getApiBaseUrl for dynamic API endpoint
import { getApiBaseUrl } from '../../config/region';
import { useTranslation } from 'react-i18next';
import { T } from '../T';
import ProfileScreen from './ProfileScreen';
import HamburgerDrawer from './HamburgerDrawer';
import LanguageSwitcher from '../LanguageSwitcher';
import honestleeLogo from '../../assets/images/thumbnails/honestlee_logo-cropped.svg';
import haloImage from '../../assets/images/thumbnails/halo.png';

const Header = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [screenSize, setScreenSize] = useState({
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
        isDesktop: window.innerWidth >= 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('venue_app_token');
      if (!token) return;

      try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", `Bearer ${token}`);
        myHeaders.append("x-region", "th"); // ✅ PHASE 4: Fixed region from india to th

        // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
        const response = await fetch(`${getApiBaseUrl()}/users/me`, {
          method: "GET",
          headers: myHeaders,
          redirect: "follow"
        });

        if (response.ok) {
          const result = await response.json();
          setProfileData(result);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('[data-dropdown-container]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isDropdownOpen]);

  // Close dropdown when profile modal opens
  useEffect(() => {
    if (showProfile) {
      setIsDropdownOpen(false);
    }
  }, [showProfile]);

  const styles = {
    header: {
      background: 'linear-gradient(to right,rgb(255, 255, 255), #f5933d)',
      zIndex: 1000, // ✅ FIXED: Increased from 40 to 1000
      position: 'relative',
    },
    container: {
      padding: screenSize.isMobile ? '8px' : '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: screenSize.isMobile ? '8px' : '12px',
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
      gap: screenSize.isMobile ? '8px' : '12px',
      flexShrink: 1,
      minWidth: 0,
    },
    titleContainer: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    title: {
      fontSize: screenSize.isMobile ? '16px' : '20px',
      fontWeight: 'bold',
      color: 'white',
      margin: 0,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    subtitle: {
      color: '#184a99',
      fontSize: screenSize.isMobile ? '12px' : '14px',
      margin: 0,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'none',
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0,
    },
    buttonBase: {
      padding: screenSize.isMobile ? '6px' : '8px',
      borderRadius: '50%',
      backgroundColor: '#184a99',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    userButton: {
      display: 'flex',
      alignItems: 'center',
      gap: screenSize.isMobile ? '6px' : '8px',
      padding: screenSize.isMobile ? '4px 8px 4px 4px' : '6px 12px 6px 6px',
      borderRadius: '50px',
      backgroundColor: '#184a99',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      flexShrink: 0,
      minWidth: 0,
      maxWidth: screenSize.isMobile ? '150px' : '200px',
    },
    profileImage: {
      width: screenSize.isMobile ? '28px' : '32px',
      height: screenSize.isMobile ? '28px' : '32px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      flexShrink: 0,
    },
    userIconFallback: {
      width: screenSize.isMobile ? '28px' : '32px',
      height: screenSize.isMobile ? '28px' : '32px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    userName: {
      color: 'white',
      fontSize: screenSize.isMobile ? '12px' : '14px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'none',
      minWidth: 0,
    },
  };

  const mediaQueryStyles = `
    @media (min-width: 480px) {
      .header-subtitle {
        display: block !important;
      }
      .user-name {
        display: inline !important;
      }
    }

    @media (max-width: 480px) {
      .dropdown-padding {
        padding: 4px !important;
      }
    }
  `;

  const handleMouseEnter = (e) => { 
    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'; 
  }
  const handleMouseLeave = (e) => { 
    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'; 
  }

  const handleProfileClick = () => {
    setShowProfile(true);
    setIsDropdownOpen(false);
  };

  const handleSignOut = () => {
    logout();
    setIsDropdownOpen(false);
  };

  // Get display name
  const getDisplayName = () => {
    return profileData?.name || user?.name || t('common.user');
  };

  // Get profile image
  const getProfileImage = () => {
    return profileData?.profileImage || user?.profileImage;
  };

  return (
    <>
      <style>{mediaQueryStyles}</style>

      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        style={styles.header}
      >
        <div style={styles.container}>
          {/* Left Section */}
          <div style={styles.leftSection}>
            <HamburgerDrawer />
            <img 
              src={honestleeLogo} 
              alt="HonestLee Logo" 
              style={{
                height: screenSize.isMobile ? '28px' : screenSize.isTablet ? '32px' : '40px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <img 
              src={haloImage} 
              alt="Halo" 
              style={{
                height: screenSize.isMobile ? '20px' : screenSize.isTablet ? '24px' : '30px',
                width: 'auto',
                objectFit: 'contain',
                marginTop: screenSize.isMobile ? '-6px' : screenSize.isTablet ? '-7px' : '-9px'
              }}
            />
          </div>

          {/* Right Section */}
          <div style={styles.rightSection}>
            <LanguageSwitcher />
            {/* <button
              style={styles.buttonBase}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              aria-label="Search"
            >
              <MagnifyingGlassIcon style={{ height: 20, width: 20, color: 'white' }} />
            </button> */}

            {/* <button
              style={{ ...styles.buttonBase, position: 'relative' }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              aria-label="Notifications"
            >
              <BellIcon style={{ height: 20, width: 20, color: 'white' }} />
              <div style={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 12,
                height: 12,
                backgroundColor: '#ef4444',
                borderRadius: '50%'
              }} />
            </button> */}

            <div style={{ position: 'relative', minWidth: 40 }} data-dropdown-container>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={styles.userButton}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                aria-label={t('nav.userMenu')}
                title={getDisplayName()}
              >
                {/* Profile Image or Fallback */}
                {getProfileImage() ? (
                  <img 
                    src={getProfileImage()} 
                    alt="Profile" 
                    style={styles.profileImage}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div style={styles.userIconFallback}>
                    <UserIcon style={{ height: 18, width: 18, color: 'white' }} />
                  </div>
                )}

                <span className="user-name" style={styles.userName}>
                  {getDisplayName()}
                </span>
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      position: 'fixed',
                      right: '16px',
                      top: '80px',
                      width: '200px',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      border: '1px solid #e5e7eb',
                      zIndex: 1001, // ✅ FIXED: Increased from 3 to 1001
                      overflow: 'hidden'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="dropdown-padding"
                  >
                    <div style={{ padding: 8 }}>
                      {/* User Info Header */}
                      <div style={{
                        padding: '12px',
                        borderBottom: '1px solid #f3f4f6',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        {getProfileImage() ? (
                          <img 
                            src={getProfileImage()} 
                            alt="Profile" 
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '2px solid #f3f4f6',
                              flexShrink: 0
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        
                        {/* Fallback for dropdown */}
                        <div 
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#f3f4f6',
                            display: getProfileImage() ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <UserIcon style={{ height: 20, width: 20, color: '#6b7280' }} />
                        </div>

                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ 
                            margin: 0, 
                            fontWeight: 600, 
                            fontSize: '14px', 
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {getDisplayName()}
                          </p>
                          <p style={{ 
                            margin: 0, 
                            fontSize: '12px', 
                            color: '#6b7280',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {profileData?.email || user?.email || t('auth.noEmail')}
                          </p>
                        </div>
                      </div>

                      {/* Profile Option */}
                      <button
                        onClick={handleProfileClick}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px',
                          color: '#374151',
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <UserIcon style={{ height: 18, width: 18, color: '#6b7280' }} />
                        <T k="nav.editProfile" />
                      </button>

                      {/* Sign Out Option */}
                      <button
                        onClick={handleSignOut}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px',
                          color: '#ef4444',
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <ArrowRightOnRectangleIcon style={{ height: 18, width: 18 }} />
                        <T k="nav.signOut" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Profile Screen */}
      <ProfileScreen 
        isOpen={showProfile} 
        onClose={() => setShowProfile(false)} 
      />
    </>
  );
};

export default Header;